/**
 * Injectable editing guard (Collab Phase 9).
 *
 * Editing modules (text-editor, element-actions, section-manager, property-panel)
 * call these to check/acquire/release per-section locks WITHOUT importing any
 * collab code — keeping them collab-agnostic. The default implementation is
 * allow-all no-ops so solo editing is byte-identical; collab-locks installs the
 * real implementation on connect and uninstalls it on disconnect.
 *
 * Lock keys mirror the protocol contract: `s:<instanceId>` for section
 * instances, `shared:navigation` / `shared:footer` for the shared sections.
 */

import { sectionLockKey, SHARED_LOCK_KEYS } from '../shared/collab-protocol.js';

const NOOP_IMPL = {
  isLockedByPeer: () => false,
  notifyLockedDenied: () => {},
  acquire: () => true, // optimistic: solo/no-op always grants
  release: () => {}
};

let impl = NOOP_IMPL;

/** Install/remove the real lock implementation (collab-locks owns it). */
export function setCollabGuardImpl(next) {
  impl = next || NOOP_IMPL;
}

/** Lock key for a section instance. */
export function lockKeyForInstance(instanceId) {
  return sectionLockKey(instanceId);
}

/** Lock key for a shared section ('navigation' | 'footer'). */
export function lockKeyForShared(kind) {
  return SHARED_LOCK_KEYS[kind] || `shared:${kind}`;
}

/** True when a PEER currently holds this section's lock (deny editing). */
export function isSectionLockedByPeer(sectionKey) {
  try {
    return !!sectionKey && impl.isLockedByPeer(sectionKey);
  } catch {
    return false;
  }
}

/** Surface the "X is editing" treatment for a denied acquire. */
export function notifyLockedDenied(sectionKey) {
  try {
    impl.notifyLockedDenied(sectionKey);
  } catch {
    /* toast failure is non-fatal */
  }
}

/** Optimistically acquire (a lost race surfaces later as op-rejected lock_held). */
export function acquireSectionLock(sectionKey) {
  try {
    return sectionKey ? impl.acquire(sectionKey) : true;
  } catch {
    return true;
  }
}

/** Release a held lock (idempotent). */
export function releaseSectionLock(sectionKey) {
  try {
    if (sectionKey) impl.release(sectionKey);
  } catch {
    /* non-fatal */
  }
}

/**
 * The common editing-entry gate: returns false (and surfaces the "editing"
 * treatment) when a peer holds the lock; otherwise optimistically acquires and
 * returns true. Editing modules call this before starting an edit.
 */
export function tryEnterSectionEdit(sectionKey) {
  if (isSectionLockedByPeer(sectionKey)) {
    notifyLockedDenied(sectionKey);
    return false;
  }
  return acquireSectionLock(sectionKey) !== false;
}
